<script setup>
import Button from "primevue/button";
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    item: Object,
    theme: Object,
    roundness: String
})

const emit = defineEmits(['book'])

const units = [
        { label: trans('rental.card_per_day'), value: 'day', and_more: trans('rental.unit_day_more') },
        { label: trans('rental.card_per_weekend'), value: 'weekend', and_more: trans('rental.unit_weekend_more') },
        { label: trans('rental.card_per_week'), value: 'week', and_more: trans('rental.unit_week_more') },
        { label: trans('rental.card_per_month'), value: 'month', and_more: trans('rental.unit_month_more') },
        { label: trans('rental.card_per_hour'), value: 'hour', and_more: trans('rental.unit_hour_more') },
        { label: trans('rental.card_per_minute'), value: 'minute', and_more: trans('rental.unit_minute_more') },
    ]

// Quick Look modal state
import Dialog from "primevue/dialog";
import { ref, computed } from "vue";

const showQuickLook = ref(false);
const selectedImageIndex = ref(0);

const hasGallery = computed(() => Array.isArray(props.item.gallery) && props.item.gallery.length > 0);
const mainImageSrc = computed(() => {
    if (!hasGallery.value) {
        return null;
    }
    const img = props.item.gallery[selectedImageIndex.value];
    return img?.full_url ?? img;
});

function openQuickLook() {
    selectedImageIndex.value = 0;
    showQuickLook.value = true;
}

function selectThumb(i) {
    selectedImageIndex.value = i;
}
</script>

<template>
    <div class="border overflow-hidden relative dark:border-neutral-700 flex flex-col" :class="roundness">
        <div class="!h-[350px] object-cover w-full">
            <img v-if="item.gallery[0]?.full_url" :src="item.gallery[0]?.full_url" alt="Item Image" class="w-full object-cover h-full"/>
            <div v-else class="bg-neutral-50 h-full w-full pi pi-camera flex justify-center items-center !text-3xl text-neutral-500"></div>
        </div>
        <div class="h-[350px] top-0 absolute bg-gradient-to-b from-transparent to-black/50 p-4 flex flex-col justify-between w-full">
            <div class="flex">
            </div>
            <div class="font-bold text-white text-xl">
                <i :class="item.type" class="mb-2"></i>
                <p>{{ item.name }}</p>
            </div>
        </div>
        <div class="info p-4 bg-white dark:bg-black grow">
            <p class="description  leading-relaxed text-wrap mb-4" v-html="item.description"></p>
            <div class="flex flex-wrap gap-2 mb-4">
                <template v-for="(attribute, index) in item.attributes" :key="index">
                    <div
                        v-if="attribute.is_visible"
                        class="rounded-full px-3 py-1 text-sm flex items-center gap-1 bg-gray-100 dark:bg-neutral-800 dark:text-gray-100 text-gray-800"
                        :class="attribute.is_highlighted ? `!bg-${theme.color}-200 dark:!bg-${theme.color}-800` : ''"
                    >
                        <i :class="attribute.icon" class="text-sm"></i>
                        <span>{{ attribute.label }}:</span>
                        <span class="font-bold">{{ attribute.value }}</span>
                    </div>
                </template>
                <p v-if="!item.attributes?.some(a => a.is_visible)" class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.no_attributes') }}</p>
            </div>

        </div>
        <div class="flex justify-between flex-col gap-3 border-t p-4 dark:bg-neutral-800 dark:border-neutral-700 bg-neutral-50">
            <div class="flex flex-col gap-0.5">
                <div class="flex gap-2 text-xl font-bold">
                    <span class="opacity-70 dark:opacity-90">{{ $t('rental.price_from') }} {{ Intl.NumberFormat('cs-CZ', { style: 'currency', currency: item.prices[0]?.currency }).format(item.prices[0]?.unit_price) }}</span>
                    <span class="opacity-45 dark:opacity-50">{{ $t('rental.price_per') }} {{ units.find(unit => unit.value === item.unit)?.label }}</span>
                </div>
                <p v-if="item.prices[0]?.vat_percentage > 0" class="text-xs text-gray-500 dark:text-gray-400">
                    {{ $t('rental.price_with_vat') }} {{ Intl.NumberFormat('cs-CZ', { style: 'currency', currency: item.prices[0]?.currency }).format(item.prices[0]?.unit_price * (1 + item.prices[0]?.vat_percentage / 100)) }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <Button
                    :class="[`!bg-${theme.color}-500 !px-6 !py-3 !border-none hover:!bg-${theme.color}-600`, roundness]"
                    :label="$t('widget.book')"
                    fluid
                    @click="emit('book', item)"
                ></Button>
                <Button
                    :class="[roundness]"
                    icon="pi pi-eye"
                    severity="secondary"
                    class="!h-[45px] !w-[50px]"
                    outlined
                    :aria-label="'Quick look'"
                    @click="openQuickLook"
                />
            </div>
        </div>

        <!-- Quick Look Modal -->
        <Dialog v-model:visible="showQuickLook" modal :draggable="false"
                class="md:max-w-[1100px] w-[96vw]" position="center" :header="false">
            <template #container>
                <div class=" relative">
                    <div class="fixed top-4 right-4 z-10">
                        <Button icon="pi pi-times" rounded severity="secondary" @click="showQuickLook = false"></Button>
                    </div>

                    <!-- VERTIKÁLNÍ LAYOUT: masivní galerie nahoře, informace níže -->
                    <div class="flex flex-col gap-6">
                        <!-- Hlavní galerie -->
                        <div class="w-full rounded-xl rounded-b-none overflow-hidden bg-neutral-100 dark:bg-neutral-900 border dark:border-neutral-800">
                            <div class="w-full">
                                <div
                                    v-if="mainImageSrc" class="relative h-[40vh] md:h-[50vh] overflow-hidden">
                                    <img
                                        :src="mainImageSrc"
                                        class="w-full h-full absolute top-0 brightness-40 object-cover"
                                        :alt="item.name"
                                    />

                                    <img
                                        :src="mainImageSrc"
                                        class="w-full h-full absolute object-contain backdrop-blur"
                                        :alt="item.name"
                                    />
                                </div>
                                <div v-else class="w-full h-[56vh] md:h-[62vh] flex items-center justify-center text-neutral-500">
                                    <i class="pi pi-images text-4xl"></i>
                                </div>
                            </div>
                            <!-- Thumbs -->
                            <div v-if="hasGallery" class="flex gap-2 overflow-x-auto p-3 bg-white/70 dark:bg-black/30">
                                <button
                                    v-for="(img, i) in item.gallery"
                                    :key="'thumb-'+i"
                                    class="h-[76px] min-w-[110px] rounded-md overflow-hidden border relative"
                                    :class="i === selectedImageIndex ? `ring-2 ring-${theme.color}-500 border-${theme.color}-500` : 'border-neutral-200 dark:border-neutral-700'"
                                    @click="selectThumb(i)"
                                    aria-label="Select image"
                                >
                                    <img :src="img?.full_url ?? img" class="w-full h-full object-cover" :alt="item.name + ' ' + (i+1)"/>
                                </button>
                            </div>
                        </div>

                        <!-- Detaily -->
                        <div class="flex flex-col gap-5 p-6 pt-0">
                            <!-- Název a popis -->
                            <div>
                                <p class="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white leading-tight">
                                    {{ item.name }}
                                </p>
                                <div v-if="item.description" class="text-gray-700 dark:text-gray-300 leading-relaxed mt-2" v-html="item.description">
                                </div>
                            </div>

                            <!-- Vlastnosti -->
                            <div v-if="item.attributes?.length" class="flex flex-wrap gap-2">
                                <template v-for="(attribute, index) in item.attributes" :key="'ql-attr-'+index">
                                    <div
                                        v-if="attribute.is_visible"
                                        class="rounded-full px-3 py-1 text-sm flex items-center gap-1 bg-gray-100 dark:bg-neutral-800 dark:text-gray-100 text-gray-800"
                                        :class="attribute.is_highlighted ? `!bg-${theme.color}-200 dark:!bg-${theme.color}-800` : ''"
                                    >
                                        <i :class="attribute.icon" class="text-sm"></i>
                                        <span>{{ attribute.label }}:</span>
                                        <span class="font-bold">{{ attribute.value }}</span>
                                    </div>
                                </template>
                            </div>

                            <!-- Cena -->
                            <div v-if="item.prices?.length" class="border rounded-lg p-4 bg-white dark:bg-neutral-900 dark:border-neutral-700 shadow-sm">
                                <div class="flex items-baseline gap-2">
                                    <span class="text-3xl font-bold">
                                        {{ Intl.NumberFormat('cs-CZ', { style: 'currency', currency: item.prices[0]?.currency }).format(item.prices[0]?.unit_price) }}
                                    </span>
                                    <span class="text-gray-600 dark:text-gray-300">/ {{ units.find(u => u.value === item.unit)?.label }}</span>
                                </div>
                                <p v-if="item.prices[0]?.vat_percentage > 0" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {{ $t('rental.price_with_vat') }} {{ Intl.NumberFormat('cs-CZ', { style: 'currency', currency: item.prices[0]?.currency }).format(item.prices[0]?.unit_price * (1 + item.prices[0]?.vat_percentage / 100)) }} / {{ units.find(u => u.value === item.unit)?.label }} ({{ $t('rental.vat_abbr') }} {{ item.prices[0]?.vat_percentage }}%)
                                </p>
                                <p v-if="item.prices[0]?.note" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {{ item.prices[0]?.note }}
                                </p>
                            </div>

                            <!-- CTA -->
                            <div class="flex gap-2">
                                <Button
                                    :class="[`!bg-${theme.color}-500 !px-5 !py-2.5 !border-none hover:!bg-${theme.color}-600`, roundness]"
                                    :label="$t('widget.book')"
                                    @click="() => { showQuickLook = false; emit('book', item) }"
                                />
                                <Button
                                    icon="pi pi-times"
                                    outlined
                                    severity="secondary"
                                    :class="[roundness]"
                                    @click="showQuickLook = false"
                                    :aria-label="$t('widget.cancel')"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Dialog>
    </div>
</template>

<style scoped lang="scss">

</style>
